/**
 * Public email-verification page (route /verify-email, round7/signup-ui,
 * P4.5-B). NOT wrapped in <ProtectedRoute> — there is no session yet at this
 * point in the flow (the visitor has a Cognito account from /signup but
 * cannot log in until this page's call succeeds — see DockPro-Backend's
 * verifyEmailHandler.ts header for why POST, not a bare GET link).
 *
 * Matches the exact link registerHandler.ts's welcome email sends:
 * `{PORTAL_BASE_URL}/verify-email?tenantId=...&token=...` — read via
 * react-router-dom's useSearchParams rather than parsing location.search by
 * hand. Calls POST /api/verify-email on mount and renders one of three
 * states: verifying (brief), success, or error.
 *
 * === No resend-verification button (correction #3) ===
 * DockPro-Backend's DEBT.md (round7/self-registration entry) documents the
 * absence of a resend-verification-email endpoint as a deliberate, explicit
 * scope cut, not an oversight — there is nothing for a "resend" button here
 * to call. The EXPIRED_TOKEN error copy below is the backend's own message
 * ("This verification link has expired. Contact support for a new one.") —
 * shown verbatim rather than paired with a fake/broken resend action.
 *
 * === Both success shapes treated the same ===
 * verifyEmail() resolves for EITHER `{verified: true}` (a fresh
 * confirmation) or `{alreadyVerified: true}` (a repeat click on the same
 * link, e.g. opened in two tabs — verifyEmailHandler.ts is deliberately
 * idempotent here, per its own doc comment) — both mean "you're good, log
 * in," so this page shows one identical success state for both.
 *
 * === Error copy: reuse the backend's own message where it's already good ===
 * Same "check err.code first, fall back to friendlyErrorMessage" convention
 * this repo's other known-code mappings use (see SettingsPage.tsx's
 * designerPreset error handling, BillingPage.tsx's checkoutErrorMessage).
 * EXPIRED_TOKEN and INVALID_TOKEN already carry specific, safe, actionable
 * text server-side; MISSING_TENANT_ID/MISSING_TOKEN/NOT_FOUND/
 * NO_PENDING_VERIFICATION get this page's own copy since the backend's raw
 * messages for those are written for a developer, not a builder.
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { StatusMessage } from "../components/StatusMessage";
import { friendlyErrorMessage } from "../components/friendlyErrorMessage";
import { BrandHeader } from "../components/BrandHeader";
import { verifyEmail, ApiError } from "../lib/api";
import styles from "./VerifyEmailPage.module.css";

type Status = "verifying" | "success" | "error";

function verifyEmailErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    // Already specific, safe, actionable copy — shown verbatim (see this
    // file's header on why there is deliberately no resend button here).
    if (err.code === "EXPIRED_TOKEN" || err.code === "INVALID_TOKEN") {
      return err.message;
    }
    if (err.code === "NOT_FOUND") {
      return "We couldn't find an account for this verification link.";
    }
    if (err.code === "NO_PENDING_VERIFICATION") {
      return "This account doesn't have a pending email verification — you may already be verified. Try logging in.";
    }
    if (err.code === "MISSING_TENANT_ID" || err.code === "MISSING_TOKEN") {
      return "This verification link is missing information — check that you copied the whole link from your email.";
    }
  }
  return friendlyErrorMessage(err, "verifying your email");
}

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const tenantId = searchParams.get("tenantId") ?? "";
  const token = searchParams.get("token") ?? "";

  const [status, setStatus] = useState<Status>("verifying");
  const [errorMessage, setErrorMessage] = useState("");

  // Idempotent server-side (see file header), so a StrictMode double-invoke
  // of this effect in dev is harmless — both calls succeed the same way.
  useEffect(() => {
    let cancelled = false;
    verifyEmail(tenantId, token)
      .then(() => { if (!cancelled) setStatus("success"); })
      .catch((err) => {
        if (cancelled) return;
        setErrorMessage(verifyEmailErrorMessage(err));
        setStatus("error");
      });
    return () => { cancelled = true; };
  }, [tenantId, token]);

  return (
    <main className={styles.pageContainer}>
      <div className={styles.card}>
        <BrandHeader />

        <div className={styles.body}>
          {/* Every branch's <h1> is tabIndex/autoFocus: RouteFocusManager
              only refocuses on a real route change, so the "verifying" ->
              "success"/"error" transition (an in-page state change) needs
              its own focus move, same reasoning as SignupPage.tsx's
              "check your email" screen. */}
          {status === "verifying" && (
            <>
              <h1 tabIndex={-1} autoFocus>Verifying your email…</h1>
              <p className={styles.subtle}>One moment.</p>
            </>
          )}

          {status === "success" && (
            <>
              <h1 tabIndex={-1} autoFocus>Email verified</h1>
              <StatusMessage
                variant="status"
                message="Your email is verified. You can now log in."
                className={styles.success}
              />
              <Link to="/login" className={styles.textLink}>Go to login</Link>
            </>
          )}

          {status === "error" && (
            <>
              <h1 tabIndex={-1} autoFocus>We couldn&rsquo;t verify your email</h1>
              <StatusMessage variant="error" message={errorMessage} className={styles.error} />
              <p className={styles.subtle}>If you&rsquo;re stuck, contact support for help.</p>
              <Link to="/login" className={styles.textLink}>Back to login</Link>
            </>
          )}
        </div>
      </div>
    </main>
  );
}
